// src/pages/Coleta/components/ConfigurarPaginaDialog.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useBootstrap } from '../../../context/BootstrapContext';
import { configurarPaginaColeta, mensagemErro } from '../../../infra/api/Coletas';
import { ALVOS, PAGINAS_COLETA, analisesDosProtocolos, unidadesDoProtocolo } from '../../../utils/coleta';

/**
 * ADMIN escolhe a análise de protocolo que a página mostra, e qual alvo/unidade do
 * modelo vira o número da tela. É pelo ID: renomear o protocolo no IOT não quebra.
 */
export default function ConfigurarPaginaDialog({ open, pagina, config, onClose, onSalvo }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { bootstrap } = useBootstrap();
    const meta = PAGINAS_COLETA[pagina];

    const analises = useMemo(() => analisesDosProtocolos(bootstrap?.protocolos), [bootstrap?.protocolos]);

    const [analiseId, setAnaliseId] = useState('');
    const [alvo, setAlvo] = useState('');
    const [unidade, setUnidade] = useState('');
    const [bacteriaId, setBacteriaId] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');

    useEffect(() => {
        if (!open) return;
        setAnaliseId(config?.analiseId || '');
        setAlvo(config?.alvo || meta.alvoPadrao);
        setUnidade(config?.unidade || meta.unidadePadrao);
        setBacteriaId(config?.bacteriaId || '');
        setErro('');
    }, [open, config, meta]);

    const protocolo = useMemo(() => {
        const protocoloId = analises.find((a) => a.analiseId === analiseId)?.protocoloId;
        return (bootstrap?.protocolos || []).find((p) => p.id === protocoloId);
    }, [analises, analiseId, bootstrap?.protocolos]);

    // Unidades que o protocolo mede: num protocolo de leite só em UFC/mL, oferecer NMP
    // faria a página procurar uma contagem que o modelo não manda.
    const unidades = useMemo(() => unidadesDoProtocolo(protocolo), [protocolo]);
    useEffect(() => {
        if (!unidades.some((u) => u.valor === unidade)) setUnidade(unidades[0].valor);
    }, [unidades, unidade]);

    // Bactérias do laboratório que o protocolo da análise mede na unidade escolhida.
    const bacterias = useMemo(() => {
        const vistas = new Set();
        return (protocolo?.calibrationSets || []).filter((cs) => {
            const ok = cs?.bacteriaId && String(cs.unit || '').toUpperCase().startsWith(unidade) && !vistas.has(cs.bacteriaId);
            if (ok) vistas.add(cs.bacteriaId);
            return ok;
        });
    }, [protocolo, unidade]);

    // Troca de análise ou unidade pode invalidar a bactéria escolhida.
    useEffect(() => {
        if (bacteriaId && !bacterias.some((b) => b.bacteriaId === bacteriaId)) setBacteriaId('');
    }, [bacterias, bacteriaId]);

    const salvar = async () => {
        setSalvando(true);
        setErro('');
        try {
            onSalvo(await configurarPaginaColeta(pagina, { analiseId, alvo, unidade, bacteriaId }));
        } catch (e) {
            setErro(mensagemErro(e, 'Não foi possível salvar a configuração.'));
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Dialog open={open} onClose={salvando ? undefined : onClose} fullScreen={fullScreen} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontWeight: 700 }}>Configurar página · {meta.titulo}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2.5}>
                    <Typography variant="body2" color="text.secondary">
                        A página mostra só as amostras desta análise. Leite e colostro podem ser
                        análises do mesmo protocolo; cada página fica com a sua.
                    </Typography>

                    <TextField
                        select
                        fullWidth
                        label="Análise do protocolo"
                        value={analiseId}
                        onChange={(e) => setAnaliseId(e.target.value)}
                        helperText={analises.length === 0 ? 'Nenhum protocolo com análise nesta conta.' : ' '}
                    >
                        {analises.map((a) => (
                            <MenuItem key={a.analiseId} value={a.analiseId}>
                                {a.protocoloNome} — {a.analiseNome}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField select fullWidth label="Resultado exibido" value={alvo} onChange={(e) => setAlvo(e.target.value)}>
                            {ALVOS.map((a) => (
                                <MenuItem key={a.valor} value={a.valor}>{a.rotulo}</MenuItem>
                            ))}
                        </TextField>
                        <TextField select label="Unidade" value={unidade} onChange={(e) => setUnidade(e.target.value)} sx={{ minWidth: { sm: 140 } }}>
                            {unidades.map((u) => (
                                <MenuItem key={u.valor} value={u.valor}>{u.rotulo}</MenuItem>
                            ))}
                        </TextField>
                    </Stack>

                    <TextField
                        select
                        fullWidth
                        label="Bactéria do laboratório comparada"
                        value={bacteriaId}
                        onChange={(e) => setBacteriaId(e.target.value)}
                        helperText="O resultado de laboratório desta bactéria é comparado com o do modelo."
                        SelectProps={{ displayEmpty: true }}
                        InputLabelProps={{ shrink: true }}
                    >
                        <MenuItem value="">
                            <em>{bacterias.length === 1 ? `Automática (${bacterias[0].bacteriaNome})` : 'Nenhuma'}</em>
                        </MenuItem>
                        {bacterias.map((b) => (
                            <MenuItem key={b.bacteriaId} value={b.bacteriaId}>{b.bacteriaNome} · {b.unit}</MenuItem>
                        ))}
                    </TextField>

                    {erro && <Alert severity="error">{erro}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={salvando} color="inherit">Cancelar</Button>
                <Button onClick={salvar} variant="contained" disableElevation disabled={salvando || !analiseId}>
                    {salvando ? 'Salvando...' : 'Salvar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
